import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { PerimeterStreetOrientationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import { DIST_EPSILON } from "geom/core";
import type { PlanarGraph, PlanarGraphLoops } from "geom/planarGraph";
import { planarGraphHalfEdgeLength, planarGraphHalfEdgeNormal, planarGraphHalfEdgeToLine2 } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { vec2Dot } from "geom/vec2";

// the minimum protruding dim of a bumpout able to trigger classification as front or back to successive edges
const MIN_FACADE_CLASSIFICATION_CHANGE_LENGTH = 30;

export function getPerimeterStreetOrientations(
  perimeterFaces: PlanarGraphLoops,
  levelPolygonPlanarGraph: PolygonPlanarGraph,
  mirror: boolean,
): PerimeterStreetOrientationTable {
  const perimStreetOrientationTable: PerimeterStreetOrientationTable = {};

  const { planarGraph } = levelPolygonPlanarGraph;

  // iterate boundaries
  for (let i = 0, l = perimeterFaces.faceVertices.length; i < l; i++) {
    const boundaryFaceHalfEdges = perimeterFaces.faceHalfEdges[i];
    const boundaryFaceHalfEdgesCount = boundaryFaceHalfEdges.length;
    const edgesFrontAndSideInfo = getBuildingFrontAndSideInfo(planarGraph, boundaryFaceHalfEdges, 0.0, -1.0, mirror);

    // iterate the edges of the perimeter
    for (let j = 0, c = boundaryFaceHalfEdgesCount; j < c; j++) {
      const currJ = j;
      const halfEdge = boundaryFaceHalfEdges[currJ];
      const orientation = edgesFrontAndSideInfo[halfEdge];

      orientation === undefined
        ? (perimStreetOrientationTable[halfEdge] = StreetOrientation.BACK)
        : (perimStreetOrientationTable[halfEdge] = orientation);
    }
  }

  return perimStreetOrientationTable;
}

export function getBuildingFrontAndSideInfo(
  planarGraph: PlanarGraph,
  faceHalfEdges: number[],
  buildingNormalX: number = 0.0,
  buildingNormalY: number = -1.0,
  mirror: boolean = false,
): StreetOrientation[] {
  const edges: StreetOrientation[] = [];
  const boundaryFaceHalfEdgesCount = faceHalfEdges.length;

  // look for the start edge (the closest front wall)
  const startEdgeIndex = getFurthestEdgeIndex(planarGraph, faceHalfEdges, buildingNormalX, buildingNormalY, mirror);

  const addBuildingFrontInfo = (halfEdge: number, rightSide: boolean): boolean => {
    const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge, true);
    const edgeLength = planarGraphHalfEdgeLength(planarGraph, halfEdge);
    const dot = vec2Dot(buildingNormalX, buildingNormalY, edgeNormalX, edgeNormalY);

    if (dot <= -DIST_EPSILON && edgeLength > MIN_FACADE_CLASSIFICATION_CHANGE_LENGTH) {
      // back - classify any previousEdges as side then stop
      for (let j = 0, lj = prevHalfEdges.length; j < lj; j++) {
        edges[prevHalfEdges[j]] = rightSide ? StreetOrientation.RIGHT : StreetOrientation.LEFT;
      }
      prevHalfEdges = [];

      return false;
    } else if (dot >= DIST_EPSILON && edgeLength > MIN_FACADE_CLASSIFICATION_CHANGE_LENGTH) {
      // front - classify any previousEdges and this edge as front
      for (let j = 0, lj = prevHalfEdges.length; j < lj; j++) {
        edges[prevHalfEdges[j]] = StreetOrientation.FRONT;
      }
      prevHalfEdges = [];
      edges[halfEdge] = StreetOrientation.FRONT;
    } else {
      // add to array for either front or side
      prevHalfEdges.push(halfEdge);
    }

    return true;
  };

  // travel forward
  let prevHalfEdges: number[] = [];
  for (let i = 0; i < boundaryFaceHalfEdgesCount; i++) {
    const halfEdge = faceHalfEdges[(startEdgeIndex + i) % boundaryFaceHalfEdgesCount];
    if (!addBuildingFrontInfo(halfEdge, mirror)) {
      break;
    }
  }

  // travel backwards
  prevHalfEdges = [];
  for (let i = boundaryFaceHalfEdgesCount - 1; i >= 0; i--) {
    const halfEdge = faceHalfEdges[(startEdgeIndex + i + boundaryFaceHalfEdgesCount) % boundaryFaceHalfEdgesCount];
    if (!addBuildingFrontInfo(halfEdge, !mirror)) {
      break;
    }
  }

  return edges;
}

export function checkOrientationWithMode(
  orientation: StreetOrientation | undefined,
  mode: StreetOrientationMode,
): boolean {
  if (mode === StreetOrientationMode.ALL_FACES) {
    return true;
  }
  if (mode === StreetOrientationMode.NO_FACES) {
    return false;
  }

  switch (orientation) {
    case undefined:
      // @TODO(Andrew): find out why these values are undefined and fix it
      // we set them to FRONT for now to avoid breaking baselines
      orientation = StreetOrientation.FRONT;
      break;
    case StreetOrientation.UNDEFINED:
      return false;
    default:
      break;
  }
  switch (mode) {
    case StreetOrientationMode.FRONT_FACES:
      return orientation === StreetOrientation.FRONT;
    case StreetOrientationMode.FRONT_AND_SIDE_FACES:
      return orientation !== StreetOrientation.BACK;
    case StreetOrientationMode.FRONT_AND_BACK_FACES:
      return orientation === StreetOrientation.FRONT || orientation === StreetOrientation.BACK;
    default:
      return false;
  }
}

export function getFurthestEdgeIndex(
  planarGraph: PlanarGraph,
  faceHalfEdges: number[],
  buildingNormalX: number = 0.0,
  buildingNormalY: number = -1.0,
  reverseOrder: boolean = false,
): number {
  let startEdgeIndex = 0;

  const boundaryFaceHalfEdgesCount = faceHalfEdges.length;

  let bestDot = -Number.MAX_VALUE;
  for (let i = 0; i < boundaryFaceHalfEdgesCount; i++) {
    const currI = reverseOrder ? boundaryFaceHalfEdgesCount - i - 1 : i;
    const halfEdge = faceHalfEdges[currI];
    const edge = planarGraphHalfEdgeToLine2(planarGraph, halfEdge);
    const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge, true);

    if (vec2Dot(buildingNormalX, buildingNormalY, edgeNormalX, edgeNormalY) >= DIST_EPSILON) {
      const startDot = vec2Dot(buildingNormalX, buildingNormalY, edge[0], edge[1]);
      const endDot = vec2Dot(buildingNormalX, buildingNormalY, edge[2], edge[3]);
      const currDot = Math.min(startDot, endDot);

      if (currDot > bestDot) {
        bestDot = currDot;
        startEdgeIndex = currI;
      }
    }
  }

  return startEdgeIndex;
}
